// mergeBranchTemplates — pure, schema-aware WHEN-branch text synthesis used
// by the add-branch menu (AddWhenBranchMenu) and the DELETE→UPDATE action-kind
// switch (MergeActionKindDropdown). §2.5 decisions 4+5:
//   - the UPDATE-side column is the FIRST schema-snapshot target-table column
//     NOT referenced (as a whole word) in the ON predicate text — updating an
//     ON-predicate column is illegal on Oracle and useless everywhere else;
//   - the value side is `<USING alias>.<col>` when the MERGE source carries
//     an alias, else the literal `NULL`;
//   - callers compute the `AND 1 = 1` placeholder need (a same-matchKind
//     branch already exists) from the VQE-side parsed WhenBranch list (see
//     `countMatchedNotMatchedBranches` below), NOT from the resolver's
//     matched / not-matched buckets — those fold `NOT MATCHED BY SOURCE` into
//     the not-matched bucket and would over-trigger the placeholder for a
//     case the keyword itself already disambiguates.
//
// PRD: Ralph/VisualQueryEditorEdit/PRD.md § Slice: Edit — MERGE branch ops (§2.5 S7).

import { IDENT_CONT, isWordBoundaryBefore } from '../insert/insertParseHelpers';
import type { QueryStructure, SchemaSnapshot } from '../../../../sql-resolver/types';
import { lookupRelationColumns } from '../../../../sql-resolver/service/snapshotRelations';
import type { WhenBranch } from './parseMergeBranch';

export type MergeTemplateInputs = {
  /** Target-table column names, schema-snapshot order (as typed, unquoted). */
  readonly targetColumns: readonly string[];
  /** The ON predicate, joined fragment text — opaque, scanned for word matches. */
  readonly onText: string;
  /** The USING alias (as typed), or null when the source carries none. */
  readonly srcAlias: string | null;
};

/**
 * Resolves the schema-aware inputs the templates below need. Returns null
 * when the merge target isn't a plain relation or the schema snapshot has no
 * known columns for it — nothing to synthesize a template from.
 */
export function extractTemplateInputs(
  structure: QueryStructure,
  snapshot: SchemaSnapshot,
): MergeTemplateInputs | null {
  const target = structure.mergeTarget;
  if (!target || target.target.kind !== 'relation') return null;
  const columns = lookupRelationColumns(snapshot, target.target.qualified) ?? [];
  if (columns.length === 0) return null;
  const onText = (structure.mergeOn ?? []).map((f) => f.text).join(' ');
  const srcAlias = structure.mergeSource?.alias?.alias ?? null;
  return {
    targetColumns: columns.map((c) => c.qualified.name),
    onText,
    srcAlias,
  };
}

/**
 * True when `column` appears as a whole word in `text` (case-insensitive).
 * Uses `IDENT_CONT` (which includes `_`) for the trailing boundary so a
 * snake_case neighbor never produces a false match — the naive `\bcol\b`
 * regex mistake this repo has hit before.
 */
function isColumnReferenced(text: string, column: string): boolean {
  const lowerText = text.toLowerCase();
  const lowerCol = column.toLowerCase();
  if (lowerCol.length === 0) return false;
  let from = 0;
  for (;;) {
    const idx = lowerText.indexOf(lowerCol, from);
    if (idx < 0) return false;
    const end = idx + lowerCol.length;
    const boundaryAfter = end >= text.length || !IDENT_CONT.test(text[end]);
    if (isWordBoundaryBefore(text, idx) && boundaryAfter) return true;
    from = idx + 1;
  }
}

/** Decision 4: the first target column NOT referenced in the ON predicate text. Null when every column is referenced. */
export function pickUnreferencedColumn(inputs: MergeTemplateInputs): string | null {
  return inputs.targetColumns.find((c) => !isColumnReferenced(inputs.onText, c)) ?? null;
}

function rhsFor(inputs: MergeTemplateInputs, column: string): string {
  return inputs.srcAlias ? `${inputs.srcAlias}.${column}` : 'NULL';
}

/**
 * `UPDATE SET <col> = <src>.<col>` (no `WHEN … THEN` wrapper) — the
 * DELETE→UPDATE action-switch body. Null when every target column is
 * ON-referenced (nothing safe to update).
 *
 * By-source carve-out: a `WHEN NOT MATCHED BY SOURCE` arm has, by
 * definition, no matching source row — SQL Server rejects any source-alias
 * column reference there (confirmed live: "The multi-part identifier could
 * not be bound"). For that matchKind the RHS reuses the no-alias fallback
 * (`NULL`); the column-choice rule is unchanged. A NOT NULL column then
 * errors legibly at run time — the template is a user-editable placeholder,
 * consistent with the no-preemptive-gating posture.
 */
export function buildUpdateAssignmentText(
  inputs: MergeTemplateInputs,
  matchKind: WhenBranch['matchKind'] = 'matched',
): string | null {
  const col = pickUnreferencedColumn(inputs);
  if (!col) return null;
  const effective = matchKind === 'not-matched-by-source' ? { ...inputs, srcAlias: null } : inputs;
  return `UPDATE SET ${col} = ${rhsFor(effective, col)}`;
}

function andPlaceholder(needsAnd: boolean): string {
  return needsAnd ? ' AND 1 = 1' : '';
}

/** Decision 5 add-branch template: `WHEN MATCHED [AND 1 = 1] THEN UPDATE SET <col> = <src>.<col>`. */
export function buildMatchedUpdateBranchText(
  inputs: MergeTemplateInputs,
  needsAnd: boolean,
): string | null {
  const assignment = buildUpdateAssignmentText(inputs);
  return assignment ? `WHEN MATCHED${andPlaceholder(needsAnd)} THEN ${assignment}` : null;
}

/** Decision 5 add-branch template: `WHEN MATCHED [AND 1 = 1] THEN DELETE`. */
export function buildMatchedDeleteBranchText(needsAnd: boolean): string {
  return `WHEN MATCHED${andPlaceholder(needsAnd)} THEN DELETE`;
}

/** Decision 5 add-branch template: `WHEN NOT MATCHED [AND 1 = 1] THEN INSERT (<cols>) VALUES (<src>.<col>, …)`. */
export function buildNotMatchedInsertBranchText(
  inputs: MergeTemplateInputs,
  needsAnd: boolean,
): string | null {
  if (inputs.targetColumns.length === 0) return null;
  const cols = inputs.targetColumns.join(', ');
  const values = inputs.targetColumns.map((c) => rhsFor(inputs, c)).join(', ');
  return `WHEN NOT MATCHED${andPlaceholder(needsAnd)} THEN INSERT (${cols}) VALUES (${values})`;
}

/**
 * Doc-order matched / plain-not-matched counts from the VQE-side parsed
 * branch list — EXCLUDES `not-matched-by-source` (see file header): that arm
 * is disambiguated by its own keyword phrase, so it never forces the `AND`
 * placeholder onto a sibling plain `NOT MATCHED` branch, and Oracle (which
 * has no `BY SOURCE` arm) never sees it either.
 */
export function countMatchedNotMatchedBranches(
  branches: readonly WhenBranch[],
): { readonly matchedCount: number; readonly notMatchedCount: number } {
  let matchedCount = 0;
  let notMatchedCount = 0;
  for (const b of branches) {
    if (b.matchKind === 'matched') matchedCount++;
    else if (b.matchKind === 'not-matched') notMatchedCount++;
  }
  return { matchedCount, notMatchedCount };
}
